// React
import { useEffect, useState } from "react";
// Redux
import { useSelector } from "react-redux";
// React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// MUI
import { Stack } from "@mui/material";

// API
import { getProviderList, onLoginProvider, updateAdministrationTools } from "@Settings/api/AdministrationTools";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useLoginProvider from "@Settings/hooks/useLoginProvider";

// Helpers
import { getSelectedProvider, hasSomeRoleLoggedIn } from "@Settings/helpers/AdministrationTools/Provider/utils";

// Components
import LoggedRoles from "@Settings/Layouts/LoggedRoles";
import ConfigurationSets from "@Settings/components/AdministrationTools/GlobalPositioningService/ConfigurationSets";
import ProviderSection from "@Settings/Layouts/ProviderSection";
import LotInformation from "./sections/LotInformation";

// Constants
import { EventTypes } from "@Settings/Constants/AdministrationTools/AdministrationTools";

// Types
import { RootState } from "@/redux/store";
import { CapitalizedRoles } from "@Settings/types/Common/auth";
import { CustomCredentials, GPSSelectedProvider, TSelectedProvider } from "@Settings/types/administrationTools/utils";
import { TSelectValue } from "@Layouts/components/InputComponents/SmartSelector/interfaces";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { IAdministrationTools, IGlobalPositioningService, ILojack } from "@Settings/types/administrationTools/model";
import GPSStore from "@Settings/components/AdministrationTools/GlobalPositioningService/sections/GPSStore.tsx";
import { onUpdateGpsStore } from "@Settings/api/AdministrationTools/subscriptions.ts";
import { TGetProviderList } from "@Settings/api/AdministrationTools/queries.ts";

type AssetManagementSystemProps = {
	isExpanded: boolean;
	administrationTools: IAdministrationTools;
};

export default function GlobalPositioningService(props: Readonly<AssetManagementSystemProps>) {
	const queryClient = useQueryClient();
	const { addSubscription, deleteSubscription, getSubscription } = useSubscriptions();

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	const globalPositioningService = props.administrationTools?.integrations?.globalPositioningService;

	const [isOpen, setIsOpen] = useState(false);
	const [selectedProvider, setSelectedProvider] = useState<Partial<TSelectedProvider> | null>(null);

	/***************************************** QUERIES *****************************************/
	const { data: providers, isLoading: isLoadingProviders } = useQuery({
		queryKey: ["globalPositioningService", locationId],
		queryFn: async () => getProviderList(locationId as string, "GPS"),
		enabled: Boolean(locationId) && props.isExpanded,
		staleTime: Infinity,
	});

	const updatedSelectedProvider = getSelectedProvider<GPSSelectedProvider>({
		selectedProvider,
		providers: (providers ?? []) as TGetProviderList[],
		adminToolsProvider: props.administrationTools?.integrations?.globalPositioningService,
	});

	/***************************************** MUTATIONS *****************************************/
	const { mutate: mutateGlobalPositioningService, isPending: isMutatingGPS } = useMutation({
		mutationKey: ["updateGlobalPositioningService"],
		mutationFn: async (input: Partial<IAdministrationTools>) => updateAdministrationTools(locationId as string, input),
	});

	const {
		mutate: loginProvider,
		isPending: isSigningIn,
		isError: isLoginError,
		error: loginErrorData,
		reset: resetLoginError,
		isSuccess: isLoginSuccess,
	} = useLoginProvider({
		locationId: locationId as string,
		provider: updatedSelectedProvider as GPSSelectedProvider,
		mutationKey: ["loginProvider", locationId as string],
		integrationProviderProperty: "globalPositioningService",
		onSuccess: () => {
			setIsOpen(false);
		},
	});

	/********************************************* SUBSCRIPTIONS ************************************************/
	const globalPositioningServiceSubscription = getSubscription(["globalPositioningServiceSubscription", locationId]);

	// OnUpdateSetup
	globalPositioningServiceSubscription?.subscriptions?.onLoginProvider?.setCallback(
		(result: GraphqlSubscriptionResponse<"onLoginProvider", IAdministrationTools>) => {
			const updatedAdministrationTools = result?.data?.onLoginProvider;

			queryClient.setQueryData(["administrationTools", locationId], () => updatedAdministrationTools);
		}
	);

	// OnUpdateGpsStore
	globalPositioningServiceSubscription?.subscriptions?.onUpdateGpsStore?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateGpsStore", IAdministrationTools>) => {
			const updatedAdministrationTools = result?.data?.onUpdateGpsStore;

			queryClient.setQueryData(["administrationTools", locationId], () => updatedAdministrationTools);
		}
	);

	globalPositioningServiceSubscription?.subscriptions?.onUpdateGpsStore?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateGpsStore", IAdministrationTools>) => {
			const updatedAdministrationTools = result?.data?.onUpdateGpsStore;

			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				const oldGsStoreId = updatedSelectedProvider?.gpsStoreId;

				if (updatedSelectedProvider?.providerName) {
					const newGpsStoreId = (
						updatedAdministrationTools?.integrations?.globalPositioningService?.[
							updatedSelectedProvider?.providerDBProperty as keyof IGlobalPositioningService
						] as { gpsStoreId: string }
					)?.gpsStoreId;

					return {
						...updatedAdministrationTools,
						integrations: {
							...updatedAdministrationTools?.integrations,
							globalPositioningService: {
								...updatedAdministrationTools?.integrations.globalPositioningService,
								lots: oldGsStoreId === newGpsStoreId ? cachedData.integrations.globalPositioningService.lots : [],
							},
						},
					};
				}

				return cachedData;
			});
		}
	);

	/***************************************** HANDLERS *****************************************/
	const handleClickLogin = () => {
		setIsOpen(true);
	};

	const handleChangeProvider = (provider: Partial<TSelectedProvider>) => {
		setSelectedProvider(provider);
	};

	const handleLoginGPS = (data: CustomCredentials) => {
		loginProvider({ ...data, operation: { role: updatedSelectedProvider?.role ?? "", eventType: EventTypes.LOGIN } });
	};

	const handleLogoutGPS = (newRole?: CapitalizedRoles) => {
		loginProvider({ operation: { role: newRole ?? updatedSelectedProvider?.role ?? "", eventType: EventTypes.LOGOUT } });
	};

	const handleCloseDialog = () => {
		setIsOpen(false);
		resetLoginError();
	};

	// Allowable Distance (Section 2)
	const handleUpdateAllowableDistance = (value: string | TSelectValue, property: "distance" | "unit", providerDBProperty: string) => {
		let valueToSave;
		if (property === "distance") {
			valueToSave = Number(value);
		} else {
			valueToSave = (value as { value: string })?.value;
		}

		const input: Partial<IAdministrationTools> = {
			integrations: {
				...props.administrationTools?.integrations,
				globalPositioningService: {
					...globalPositioningService,
					[providerDBProperty]: {
						...globalPositioningService?.[providerDBProperty as keyof typeof globalPositioningService],
						allowableDistance: {
							...(globalPositioningService?.[providerDBProperty as keyof typeof globalPositioningService] as ILojack)
								?.allowableDistance,
							[property]: valueToSave,
						},
					},
				},
			},
		};

		mutateGlobalPositioningService(input);
	};

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["globalPositioningServiceSubscription", locationId],
				[
					{
						context: "onLoginProvider",
						query: onLoginProvider,
						variables: {
							locationId,
						},
					},
					{
						context: "onUpdateGpsStore",
						query: onUpdateGpsStore,
						variables: {
							locationId,
						},
					},
				]
			);
		}

		return () => {
			deleteSubscription(["globalPositioningServiceSubscription", locationId]);
		};
	}, [locationId]);

	useEffect(() => {
		if (isLoginSuccess) {
			setIsOpen(false);
		}
	}, [isLoginSuccess]);

	return (
		<Stack component={"article"}>
			<ProviderSection
				isOpenDialog={isOpen}
				isLoginSuccess={isLoginSuccess}
				isSigningIn={isSigningIn}
				selectedProvider={updatedSelectedProvider}
				isLoadingProviders={isLoadingProviders}
				isMutatingProvider={isMutatingGPS}
				resetLoginError={resetLoginError}
				loginError={isLoginError}
				loginErrorData={loginErrorData}
				providers={(providers ?? []) as TGetProviderList[]}
				// Callbacks
				onCloseDialog={handleCloseDialog}
				onLogin={handleLoginGPS}
				onLogout={handleLogoutGPS}
				onChangeProvider={handleChangeProvider}
				onClickProviderLogin={handleClickLogin}
			/>

			<LoggedRoles selectedProvider={updatedSelectedProvider} />

			<GPSStore locationId={locationId as string} selectedProvider={updatedSelectedProvider} />

			<LotInformation
				locationId={locationId as string}
				lots={props.administrationTools?.integrations?.globalPositioningService?.lots}
				selectedProvider={updatedSelectedProvider}
			/>

			{hasSomeRoleLoggedIn(updatedSelectedProvider) && (
				<ConfigurationSets
					isLoading={isSigningIn || isMutatingGPS}
					selectedProvider={updatedSelectedProvider as GPSSelectedProvider}
					// Callbacks
					onUpdateAllowableDistance={handleUpdateAllowableDistance}
				/>
			)}
		</Stack>
	);
}
